import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { activateBarcode } from '../services/profile';
import RegisterKitConfirmDialog from '../components/RegisterKitConfirmDialog';

export default function RegisterNewTestRecipientPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedKit = location.state?.selectedKit || { id: '', name: '' };
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const activationInFlightRef = React.useRef(false);
  const displayBarcode = selectedKit?.id || '-';
  const displayTestType = selectedKit?.name || '-';

  const trimmedFirst = firstName.trim();
  const trimmedLast = lastName.trim();
  const trimmedEmail = email.trim();
  const formComplete = Boolean(trimmedFirst && trimmedLast && trimmedEmail);

  const confirmRegisteredNameLabel = React.useMemo(() => {
    const full = [trimmedFirst, trimmedLast].filter(Boolean).join(' ');
    return full || '—';
  }, [trimmedFirst, trimmedLast]);

  const handleOpenConfirm = React.useCallback(() => {
    if (!formComplete) {
      toast.error('Vul voornaam, achternaam en e-mail in.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      toast.error('Vul een geldig e-mailadres in.');
      return;
    }
    setShowConfirmModal(true);
  }, [formComplete, trimmedEmail]);

  const handleBackFromConfirm = React.useCallback(() => {
    if (isSubmitting) return;
    setShowConfirmModal(false);
  }, [isSubmitting]);

  const handleConfirmRegister = React.useCallback(async () => {
    if (!formComplete) return;
    if (activationInFlightRef.current) return;

    activationInFlightRef.current = true;
    setIsSubmitting(true);
    try {
      const bloodTestId = selectedKit?.bloodTestId ?? selectedKit?.rawId ?? null;
      await activateBarcode({
        blood_test_id: bloodTestId,
        barcode_text: selectedKit?.id || '',
        first_name: trimmedFirst,
        last_name: trimmedLast,
        email: trimmedEmail,
      });
      toast.success('Barcode succesvol geactiveerd.');
      setShowConfirmModal(false);
      navigate('/register-new-test/success', {
        state: {
          selectedKit,
          recipient: {
            firstName: trimmedFirst,
            lastName: trimmedLast,
            email: trimmedEmail,
          },
        },
      });
    } catch (err) {
      const data = err?.response?.data;
      const message =
        (typeof data?.detail === 'string' && data.detail) ||
        data?.message ||
        data?.error ||
        err?.message ||
        'Barcode activeren mislukt. Probeer het opnieuw.';
      toast.error(message);
    } finally {
      activationInFlightRef.current = false;
      setIsSubmitting(false);
    }
  }, [formComplete, navigate, selectedKit, trimmedEmail, trimmedFirst, trimmedLast]);

  return (
    <div className="min-h-screen bg-[#F9F5F2] px-4 py-8 md:py-12">
      <div className="max-w-3xl mx-auto mb-5">
        <h1 className="text-[#1E2426] font-libreBaskerville text-2xl md:text-3xl text-center">
        Testkit Registreren
        </h1>
      </div>
      <div className="mx-auto max-w-4xl bg-white rounded-2xl shadow-sm overflow-hidden border border-[#D1D5DB]">
        <div className="bg-[#119975] text-white px-6 md:px-7 py-4 md:py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-poppins font-semibold text-xl md:text-2xl leading-tight text-white">
                {displayBarcode}
              </p>
              <p className="font-poppins font-semibold text-sm md:text-[15px] leading-snug text-white/90 mt-1">
                {displayTestType}
              </p>
            </div>
          </div>
        </div>
        {showConfirmModal && selectedKit?.id ? (
          <div className="px-6 md:px-7 py-6 md:py-7">
            <RegisterKitConfirmDialog
              selectedKit={selectedKit}
              registeredNameLabel={confirmRegisteredNameLabel}
              resultsDestinationLabel={trimmedEmail}
              onBack={handleBackFromConfirm}
              onConfirm={handleConfirmRegister}
              isSubmitting={isSubmitting}
              titleId="confirm-register-recipient-title"
            />
          </div>
        ) : (
          <div className="px-6 md:px-7 py-6 md:py-7">
            <div className="flex items-center gap-4 mb-6 md:mb-8">
              <button
                type="button"
                onClick={() => navigate('/register-new-test')}
                className="w-10 h-10 rounded-full border border-[#D9E0E6] text-[#64748B] flex items-center justify-center"
                aria-label="Terug"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M10 3L5 8L10 13"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <h1 className="text-[#1E2426] font-poppins font-semibold text-lg md:text-xl leading-tight">
                Gegevens ontvanger
              </h1>
            </div>

            <p className="text-[#6F7A82] font-poppins text-sm md:text-[14px] leading-6 mb-7 md:mb-8 max-w-2xl">
              De ontvanger krijgt een e-mail om een account aan te maken en de resultaten te bekijken.
            </p>

            <form className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#7D8894] font-poppins font-medium text-xs md:text-sm mb-1.5">
                    Voornaam
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full h-12 rounded-xl border border-[#C9D1D9] bg-white px-4 text-[#1E2426] font-poppins text-base leading-none outline-none"
                  />
                </div>

                <div>
                  <label className="block text-[#7D8894] font-poppins font-medium text-xs md:text-sm mb-1.5">
                    Achternaam
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full h-12 rounded-xl border border-[#C9D1D9] bg-white px-4 text-[#1E2426] font-poppins text-base leading-none outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[#7D8894] font-poppins font-medium text-xs md:text-sm mb-1.5">
                  E-mail
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-12 rounded-xl border border-[#C9D1D9] bg-white px-4 text-[#1E2426] font-poppins text-base leading-none outline-none"
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleOpenConfirm}
                  disabled={isSubmitting || !formComplete}
                  className={`w-auto px-8 h-11 md:h-12 rounded-full font-poppins text-base md:text-lg font-medium transition-opacity ${
                    !isSubmitting && formComplete
                      ? 'bg-[#119975] text-[#FFFFFF] hover:opacity-95'
                      : 'bg-[#C7CFD7] text-white cursor-not-allowed'
                  }`}
                >
                  {isSubmitting ? 'Verwerken...' : 'Doorgaan'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
